import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, ArrowRight, DollarSign, Radar, MessageSquare } from "lucide-react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";

const RAG = { GREEN: { bg: "#dcfce7", c: "#166534" }, AMBER: { bg: "#fef3c7", c: "#92400e" }, RED: { bg: "#fee2e2", c: "#991b1b" } };

/** Manager Portfolio cockpit — one row per managed scheme. Aggregates per-scheme
 *  arrears / compliance / meeting data from existing endpoints (frontend-only). */
export default function ManagerPortfolio() {
  const [rows, setRows] = useState([]);
  const [agg, setAgg] = useState({ arrears: 0, lots: 0, expiring: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      const { data: schemes } = await api.get("/schemes");
      const filtered = schemes.filter((s) => s.my_role === "strata_manager" || s.my_role === "chair" || s.my_role === "committee");
      const results = [];
      let totalArrears = 0, totalLots = 0, totalExpiring = 0;
      const now = new Date();
      const in30d = new Date(now.getTime() + 30 * 86400_000);
      for (const s of filtered) {
        try {
          const [lev, comp, mtg, thr] = await Promise.all([
            api.get(`/schemes/${s.scheme_id}/levies`).catch(() => ({ data: [] })),
            api.get(`/schemes/${s.scheme_id}/compliance`).catch(() => ({ data: [] })),
            api.get(`/schemes/${s.scheme_id}/meetings`).catch(() => ({ data: [] })),
            api.get(`/schemes/${s.scheme_id}/messaging/threads`).catch(() => ({ data: [] })),
          ]);
          const overdueRows = (lev.data || []).filter((l) => l.status === "overdue");
          const arrears = overdueRows.reduce((sum, l) => sum + (l.total_amount || 0), 0);
          const expiring = (comp.data || []).filter((c) => {
            const d = c.due_date && new Date(c.due_date);
            return d && d >= now && d <= in30d && c.status !== "completed" && c.status !== "dismissed";
          }).length;
          const nextMtg = (mtg.data || [])
            .filter((m) => ["notice_sent", "draft", "in_progress"].includes(m.status))
            .sort((a, b) => (a.scheduled_at || "").localeCompare(b.scheduled_at || ""))[0];
          const unresponded = (thr.data || []).filter((t) => t.status === "OPEN" || t.status === "UNANSWERED").length;
          const rag = overdueRows.length > 3 || expiring > 2 ? "RED" : overdueRows.length > 0 || expiring > 0 || unresponded > 3 ? "AMBER" : "GREEN";
          results.push({ scheme: s, arrearsCount: overdueRows.length, arrears, expiring, unresponded, nextMtg, rag });
          totalArrears += arrears;
          totalLots += s.total_lots || 0;
          totalExpiring += expiring;
        } catch (_e) { /* skip failing scheme */ }
      }
      if (!mounted) return;
      setRows(results);
      setAgg({ arrears: totalArrears, lots: totalLots, expiring: totalExpiring, schemes: results.length });
      setLoading(false);
    })();
    return () => { mounted = false; };
  }, []);

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8">
        <h1 className="text-2xl font-semibold" style={{ color: "var(--navy)" }} data-testid="manager-home-title">Portfolio</h1>
        <p className="text-sm text-slate-500 mt-1">One row per managed scheme. Click any row to open its full committee view.</p>

        {loading ? (
          <div className="mt-8 h-64 rounded-xl border animate-pulse bg-slate-50" style={{ borderColor: "var(--border)" }} />
        ) : (
          <>
            <div className="mt-8 rounded-xl border bg-white overflow-hidden" style={{ borderColor: "var(--border)" }} data-testid="manager-portfolio-table">
              <table className="w-full text-sm">
                <thead style={{ background: "#f8fafc" }}>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                    <th className="px-4 py-3 font-semibold">Scheme</th>
                    <th className="px-4 py-3 font-semibold">Lots</th>
                    <th className="px-4 py-3 font-semibold">Arrears</th>
                    <th className="px-4 py-3 font-semibold">Compliance ≤30d</th>
                    <th className="px-4 py-3 font-semibold">Unresponded</th>
                    <th className="px-4 py-3 font-semibold">Next meeting</th>
                    <th className="px-4 py-3 font-semibold">Health</th>
                    <th className="px-4 py-3 font-semibold"></th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: "var(--border)" }}>
                  {rows.map((r) => (
                    <tr key={r.scheme.scheme_id} className="hover:bg-slate-50/60" data-testid={`manager-portfolio-row-${r.scheme.scheme_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold" style={{ color: "var(--navy)" }}>{r.scheme.name}</div>
                        <div className="text-xs text-slate-500">{r.scheme.state} · {r.scheme.plan_number}</div>
                      </td>
                      <td className="px-4 py-3 tabular-nums">{r.scheme.total_lots}</td>
                      <td className="px-4 py-3 tabular-nums">
                        {r.arrearsCount > 0 ? (
                          <span style={{ color: "var(--alert)" }}>{r.arrearsCount} · ${r.arrears.toLocaleString()}</span>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 tabular-nums">{r.expiring || <span className="text-slate-400">—</span>}</td>
                      <td className="px-4 py-3 tabular-nums">{r.unresponded || <span className="text-slate-400">—</span>}</td>
                      <td className="px-4 py-3">
                        {r.nextMtg ? new Date(r.nextMtg.scheduled_at).toLocaleDateString() : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-block px-2 py-0.5 rounded text-[11px] font-semibold uppercase tracking-wider" style={{ background: RAG[r.rag].bg, color: RAG[r.rag].c }}>
                          {r.rag}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <Link to={`/dashboard?scheme=${r.scheme.scheme_id}`} className="inline-flex items-center gap-1 text-sm font-semibold" style={{ color: "var(--blue)" }}
                          onClick={() => localStorage.setItem("opstrai.active_scheme_id", r.scheme.scheme_id)}>
                          Open <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-6 text-center text-slate-500 text-sm">No managed schemes yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-6 rounded-xl border bg-slate-950 p-6 text-white" data-testid="manager-portfolio-aggregate">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400">Portfolio aggregate</div>
              <div className="mt-3 flex flex-wrap gap-6 text-sm">
                <div className="flex items-center gap-2"><Building2 className="w-4 h-4" /> Managing {agg.schemes} schemes</div>
                <div className="flex items-center gap-2"><Building2 className="w-4 h-4" /> {agg.lots} total lots</div>
                <div className="flex items-center gap-2"><DollarSign className="w-4 h-4" style={{ color: "var(--alert)" }} /> ${agg.arrears.toLocaleString()} in arrears</div>
                <div className="flex items-center gap-2"><Radar className="w-4 h-4" style={{ color: "#f59e0b" }} /> {agg.expiring} compliance items expiring in ≤30d</div>
              </div>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
